import {
  FormActivityActionType,
  FormActivityLogItem,
  FormActivityLogVersionGroup
} from '@features/forms/types';
import {
  FormActivityLogApiItem,
  FormActivityLogApiResponse
} from '@features/forms/types/formActivityLogApi';

import {
  formatActivityLogDate,
  formatActivityLogDisplayDate,
  getActivityChangeCount,
  getActivityLogTitle,
  getVersionGroupTitle,
  mapModifiedDataToChanges
} from './formatActivityLogDisplay';

const normalizeActionType = (actionType: string): FormActivityActionType => {
  const normalized = actionType.toUpperCase();
  if (normalized === 'CREATE') {
    return 'CREATE';
  }
  if (normalized === 'PUBLISH') {
    return 'PUBLISH';
  }
  if (normalized === 'STATUS_UPDATE') {
    return 'STATUS_UPDATE';
  }
  return 'UPDATE';
};

const mapActivityLogItem = (
  item: FormActivityLogApiItem
): FormActivityLogItem => {
  const actionType = normalizeActionType(item.action_type);
  const modifiedData = item.modified_data ?? [];
  const changes = modifiedData.flatMap(mapModifiedDataToChanges);

  return {
    id:
      item.action_id ?? `${item.version}-${item.action_type}-${item.timestamp}`,
    version: item.version,
    actionType,
    title: getActivityLogTitle(actionType, item.version),
    activityDate: formatActivityLogDisplayDate(item.timestamp),
    dateLabel: formatActivityLogDate(item.timestamp),
    changeCount: getActivityChangeCount(modifiedData),
    changes
  };
};

const buildActivityLogGroupKey = (item: FormActivityLogItem) =>
  `${item.version}|${item.activityDate}`;

const buildActivityLogGroupId = (version: number, activityDate: string) =>
  `version-${version}-${activityDate.replace(/\//g, '-')}`;

export const groupActivityLogsByVersionAndDate = (
  items: FormActivityLogItem[]
): FormActivityLogVersionGroup[] => {
  const groupOrder: string[] = [];
  const groups = new Map<string, FormActivityLogItem[]>();

  items.forEach((item) => {
    const key = buildActivityLogGroupKey(item);
    if (!groups.has(key)) {
      groupOrder.push(key);
      groups.set(key, []);
    }
    groups.get(key)?.push(item);
  });

  return groupOrder.map((key) => {
    const groupItems = groups.get(key) ?? [];
    const version = groupItems[0]?.version ?? 0;
    const activityDate = groupItems[0]?.activityDate ?? '';

    return {
      id: buildActivityLogGroupId(version, activityDate),
      version,
      title: getVersionGroupTitle(version, groupItems),
      dateLabel: groupItems[0]?.dateLabel ?? '',
      changeCount: groupItems.reduce((sum, item) => sum + item.changeCount, 0),
      items: groupItems
    };
  });
};

export const mapFormActivityLog = (
  response: FormActivityLogApiResponse
): FormActivityLogVersionGroup[] =>
  groupActivityLogsByVersionAndDate(
    (response.data ?? []).map(mapActivityLogItem)
  );

export default mapFormActivityLog;
